import type { TradingAccount } from "@/lib/accounts/types";
import { STORAGE_INVENTORY_KEYS } from "@/lib/localStorageKeys/storage";
import type { StorageInventory } from "./inventory";

const SNAPSHOT_VERSION = 1;

export interface StorageInventorySnapshot {
  version: typeof SNAPSHOT_VERSION;
  scannedAt: number;
  accountFingerprint: string;
  inventory: StorageInventory;
}

export function accountsFingerprint(accounts: TradingAccount[]): string {
  return accounts
    .map((a) => `${a.id}:${a.companyName}:${a.serverName}`)
    .sort()
    .join("|");
}

export function loadStorageInventorySnapshot(
  accounts: TradingAccount[],
): StorageInventory | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = localStorage.getItem(STORAGE_INVENTORY_KEYS.SNAPSHOT);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StorageInventorySnapshot;
    if (parsed.version !== SNAPSHOT_VERSION) return null;
    if (parsed.accountFingerprint !== accountsFingerprint(accounts)) {
      return null;
    }
    return parsed.inventory;
  } catch {
    return null;
  }
}

export function saveStorageInventorySnapshot(
  accounts: TradingAccount[],
  inventory: StorageInventory,
): void {
  if (typeof window === "undefined") return;

  try {
    const payload: StorageInventorySnapshot = {
      version: SNAPSHOT_VERSION,
      scannedAt: Date.now(),
      accountFingerprint: accountsFingerprint(accounts),
      inventory,
    };
    localStorage.setItem(
      STORAGE_INVENTORY_KEYS.SNAPSHOT,
      JSON.stringify(payload),
    );
  } catch (e) {
    console.warn("[Storage] Failed to save inventory snapshot:", e);
  }
}

export function invalidateStorageInventorySnapshot(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_INVENTORY_KEYS.SNAPSHOT);
  } catch {
    /* ignore */
  }
}

export function snapshotScannedAt(accounts: TradingAccount[]): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_INVENTORY_KEYS.SNAPSHOT);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StorageInventorySnapshot;
    if (parsed.accountFingerprint !== accountsFingerprint(accounts)) return null;
    return parsed.scannedAt;
  } catch {
    return null;
  }
}
